/**
 * Free Trial Utility
 * ----------------------------------------------------
 * Calculates remaining trial days for products
 * using EST (America/New_York) date normalization.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2026-03-26
 */

type TrialItem = {
    product_name: string;
    package_name: string;
    start_date: string;
    renewal_date: string;
    subscription_type: string;
    features: any[];
    currency_code?: string;
  };
  
  export type TrialData = Record<string, TrialItem>;
  
  export type TrialResult =
    | {
        [key: string]: string;
        maxDays: string;
      }
    | false;
  
  function toUTCDateOnly(dateStr: string): number {
    const [year, month, day] = dateStr.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  }
  
  function getTodayESTDate(): string {
    const now = new Date();
  
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  }
  
  function isTimeTampered(): boolean {
    try {
      const now = Date.now();
      const last = Number(localStorage.getItem("lastTime") || now);
  
      localStorage.setItem("lastTime", String(now));
  
      return now < last || now - last > 1000 * 60 * 60 * 24;
    } catch {
      return false;
    }
  }
  
  function getSafeESTDate(): string {
    try {
      if (isTimeTampered()) {
        const fallback = localStorage.getItem("safeDate");
        if (fallback) return fallback;
      }
  
      const today = getTodayESTDate();
      localStorage.setItem("safeDate", today);
  
      return today;
    } catch {
      return getTodayESTDate();
    }
  }
  
  function normalizeServerDate(serverDate: string): string {
    return serverDate?.split("T")[0];
  }
  
  export function getTrialRemainingDays(
    data: TrialData,
    serverDate?: string
  ): TrialResult {
    if (!data || typeof data !== "object") return false;
  
    const result: Record<string, string> & { maxDays?: string } = {};
    let minDays: number | null = null;
  
    const todayStr = serverDate
      ? normalizeServerDate(serverDate)
      : getSafeESTDate();
  
    const todayUTC = toUTCDateOnly(todayStr);
  
    for (const key in data) {
      if (key === 'timestamp' || key === 'add_ons' || key === 'mrc' || key === 'master_features') continue;
      const item = data[key];
  
      if (!item?.package_name || item.package_name !== `${key}_free_trial`) {
        return false;
      }
      const renewalUTC = toUTCDateOnly(item.renewal_date);
  
      const diffDays = Math.max(
        0,
        Math.floor((renewalUTC - todayUTC) / (1000 * 60 * 60 * 24))
      );
  
      result[key] = `${diffDays} days`;
  
      if (minDays === null || diffDays < minDays) {
        minDays = diffDays;
      }
    }
  
    if (minDays !== null) {
        result.maxDays = `${minDays} ${minDays > 1 ? 'days' : 'day'}`;
    }
    
    return result as TrialResult;
  }